import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  EmailAuthProvider,
  GoogleAuthProvider, 
  isSignInWithEmailLink,
  linkWithPopup,
  linkWithCredential,
  signInWithEmailLink,
  signInWithPopup,
  signInAnonymously,
  onAuthStateChanged,
  signOut 
} from 'firebase/auth';
import { auth } from '../utils/firebase';
import axios from 'axios';

const AuthContext = createContext(null);
const backendUserCreatePromises = new Map();
const EMAIL_LINK_EMAIL_KEY = 'placepledge_email_link_email';
const EMAIL_LINK_REDIRECT_KEY = 'placepledge_email_link_redirect';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const getRedirectPathFromUrl = () => {
  try {
    const redirectPath = new URL(window.location.href).searchParams.get('redirect');

    if (redirectPath?.startsWith('/') && !redirectPath.startsWith('//')) {
      return redirectPath;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const normalizeAuthError = (error, fallbackMessage) => {
  const message = getFriendlyAuthErrorMessage(error, fallbackMessage);

  if (error instanceof Error && error.message === message) {
    return error;
  }

  const normalizedError = new Error(message);

  if (error && typeof error === 'object') {
    normalizedError.code = error.code;
    normalizedError.response = error.response;
  }

  return normalizedError;
};

const getFriendlyAuthErrorMessage = (error, fallbackMessage) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  switch (error?.code) {
    case 'auth/admin-restricted-operation':
      return 'Guest mode is not enabled yet. Enable Anonymous sign-in in Firebase Authentication.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled yet. Check Firebase Authentication sign-in providers.';
    default:
      return error?.message || fallbackMessage;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const createBackendUser = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.uid) {
      return;
    }

    if (!backendUserCreatePromises.has(firebaseUser.uid)) {
      const createPromise = (async () => {
        const idToken = await firebaseUser.getIdToken();

        await axios.post(
          `${window.RUNTIME_CONFIG.apiUrl}/create-user`,
          {},
          {
            headers: { Authorization: `Bearer ${idToken}` }
          }
        );
      })().finally(() => {
        backendUserCreatePromises.delete(firebaseUser.uid);
      });

      backendUserCreatePromises.set(firebaseUser.uid, createPromise);
    }

    await backendUserCreatePromises.get(firebaseUser.uid);
  }, []);

  // Fetch user details including admin status
  const fetchUserDetails = useCallback(async (firebaseUser) => {
    try {
      const idToken = await firebaseUser.getIdToken();
      const response = await axios.get(
        `${window.RUNTIME_CONFIG.apiUrl}/user`,
        {
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      // Combine Firebase user with our backend user data
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || response.data.displayName || null,
        emailVerified: firebaseUser.emailVerified,
        isAnonymous: firebaseUser.isAnonymous,
        isAdmin: response.data.isAdmin || false,
        hasPaymentMethod: response.data.hasPaymentMethod || false,
        getIdToken: firebaseUser.getIdToken.bind(firebaseUser)
      });
    } catch (error) {
      console.error('Error fetching user details:', error);

      if (error.response?.status === 404) {
        try {
          await createBackendUser(firebaseUser);
          await fetchUserDetails(firebaseUser);
          return;
        } catch (createError) {
          console.error('Error creating backend user:', createError);
        }
      }

      // Still set the user, but without admin status
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || null,
        emailVerified: firebaseUser.emailVerified,
        isAnonymous: firebaseUser.isAnonymous,
        isAdmin: false,
        hasPaymentMethod: false,
        getIdToken: firebaseUser.getIdToken.bind(firebaseUser)
      });
    }
  }, [createBackendUser]);

  useEffect(() => {
    console.log('AuthProvider mounted');
    
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        console.log('Auth state changed:', firebaseUser ? {
          email: firebaseUser.email,
          uid: firebaseUser.uid,
          emailVerified: firebaseUser.emailVerified
        } : 'No user');

        if (firebaseUser) {
          await fetchUserDetails(firebaseUser);
        } else {
          setUser(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Auth state change error:', {
          code: error.code,
          message: error.message
        });
        setError(error.message);
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => {
      console.log('Cleaning up auth subscriptions');
      unsubscribe();
    };
  }, [fetchUserDetails]);

  const login = useCallback(async () => {
    try {
      setError(null);
      console.log('Starting Google sign in...');
      
      const provider = new GoogleAuthProvider();
      
      // Add scopes
      provider.addScope('profile');
      provider.addScope('email');
      
      // Force account selection
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      console.log('Opening popup...');
      const result = auth.currentUser?.isAnonymous
        ? await linkWithPopup(auth.currentUser, provider)
        : await signInWithPopup(auth, provider);
      console.log('Sign in successful:', {
        email: result.user.email,
        uid: result.user.uid
      });

      // Create user in the database if they don't exist
      await createBackendUser(result.user);

      // Fetch user details including admin status
      await fetchUserDetails(result.user);

      return result.user;
    } catch (error) {
      console.error('Auth Error:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to sign in with Google';
      
      switch (error.code) {
        case 'auth/popup-blocked':
          errorMessage = 'Please enable popups for this website to sign in with Google';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign in was cancelled. Please try again.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized for Google sign in';
          break;
        case 'auth/operation-not-supported-in-this-environment':
          errorMessage = 'Google sign in is not supported in this environment';
          break;
        default:
          errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw error;
    }
  }, [createBackendUser, fetchUserDetails]);

  const guestLogin = useCallback(async () => {
    try {
      setError(null);

      if (auth.currentUser) {
        await fetchUserDetails(auth.currentUser);
        return auth.currentUser;
      }

      const result = await signInAnonymously(auth);
      await fetchUserDetails(result.user);

      return result.user;
    } catch (error) {
      console.error('Guest Auth Error:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setError(getFriendlyAuthErrorMessage(error, 'Failed to continue as guest'));
      throw error;
    }
  }, [fetchUserDetails]);

  const sendEmailSignInLink = useCallback(async (email, redirectPath = window.location.pathname) => {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail) {
      throw new Error('Enter your email address');
    }

    try {
      setError(null);
      await axios.post(`${window.RUNTIME_CONFIG.apiUrl}/auth/email-link`, {
        email: normalizedEmail,
        redirectPath: redirectPath || '/dashboard'
      });
      window.localStorage.setItem(EMAIL_LINK_EMAIL_KEY, normalizedEmail);
      window.localStorage.setItem(EMAIL_LINK_REDIRECT_KEY, redirectPath || '/dashboard');
    } catch (error) {
      const normalizedError = normalizeAuthError(error, 'Failed to send sign-in link');
      console.error('Email link sign in error:', {
        code: normalizedError.code,
        message: normalizedError.message
      });
      setError(normalizedError.message);
      throw normalizedError;
    }
  }, []);

  const hasEmailSignInLink = useCallback(() => {
    return isSignInWithEmailLink(auth, window.location.href);
  }, []);

  const completeEmailSignInLink = useCallback(async (emailOverride) => {
    const normalizedEmail = normalizeEmail(emailOverride || window.localStorage.getItem(EMAIL_LINK_EMAIL_KEY));

    if (!normalizedEmail) {
      throw new Error('Enter the email address you used for the sign-in link');
    }

    try {
      setError(null);
      const redirectPath = window.localStorage.getItem(EMAIL_LINK_REDIRECT_KEY) || getRedirectPathFromUrl() || '/dashboard';
      const emailLink = window.location.href;
      const result = auth.currentUser?.isAnonymous
        ? await linkWithCredential(
          auth.currentUser,
          EmailAuthProvider.credentialWithLink(normalizedEmail, emailLink)
        )
        : await signInWithEmailLink(auth, normalizedEmail, emailLink);

      await createBackendUser(result.user);
      await fetchUserDetails(result.user);
      window.localStorage.removeItem(EMAIL_LINK_EMAIL_KEY);
      window.localStorage.removeItem(EMAIL_LINK_REDIRECT_KEY);
      window.history.replaceState({}, document.title, window.location.pathname);

      return {
        user: result.user,
        redirectPath
      };
    } catch (error) {
      const normalizedError = normalizeAuthError(error, 'Failed to complete email sign-in');
      console.error('Complete email link sign in error:', {
        code: normalizedError.code,
        message: normalizedError.message
      });
      setError(normalizedError.message);
      throw normalizedError;
    }
  }, [createBackendUser, fetchUserDetails]);

  const logout = useCallback(async () => {
    try {
      console.log('Attempting to sign out...');
      await signOut(auth);
      console.log('Sign out successful');
      setUser(null);
    } catch (error) {
      console.error('Logout Error:', {
        code: error.code,
        message: error.message
      });
      setError('Failed to sign out');
      throw error;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    guestLogin,
    sendEmailSignInLink,
    hasEmailSignInLink,
    completeEmailSignInLink,
    logout,
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
